import {
  TYPE_UK_ADDRESS,
  DATE_MOVE_SUFFIX,
  SELECTED_ADDRESS,
  RADIO_VALUE_YES,
  RADIO_VALUE_NO
} from '../../../utils/constants';
import {
  CHOOSE_ADD,
  CHOOSE_ADD_YES,
  CHOOSE_ADD_NO,
  SELECT_ADDRESS_FIELD
} from '../../../components/Address/addressUtils/addressConstants';
import moment from 'moment';
import { selectors as caseSelector } from '../../cases';
import { getUkAddressFromPostCode, getUkAddress, getInternationalAddress } from './addressPayloads';

const RESIDENTIAL_STATUS_OTHER = 'other';
const TITLE_STATUS_OTHER = 'other';
const LIMITED_LIB_PARTNERSHIP = 'limited liability partnership';
const SOLE_TRADER = 'sole trader';

export const getSignatoriesPayload = (officerData, state, currentOfficerNumber) => {
  const request = {
    applier: isDefaultApplicant(currentOfficerNumber) ? RADIO_VALUE_YES : RADIO_VALUE_NO,
    auth_signatory: getFieldValue(officerData.authorisedSignatory),
    shareholder: {
      percentage: officerData.percentPersonOwnCompany
    },
    business_relationship: getBusinessRelationShip(state, officerData),
    personal_details: {
      name: {
        title: getTitleStatus(officerData),
        first_name: officerData.firstName,
        middle_name: officerData.middleName,
        last_name: officerData.lastName
      },
      dob: getDateOfBirth(officerData.dateOfBirth),
      residential_status: getResidentialStatus(officerData)
    }
  };
  if (!officerData.middleName) {
    delete request.middle_name;
  }
  if (officerData.addresses) {
    delete officerData.addresses[0].status;
  }
  request.personal_details = Object.assign({}, request.personal_details, {
    addresses: isDefaultApplicant(currentOfficerNumber)
      ? officerData.addresses
      : getAddresses(officerData, state, currentOfficerNumber)
  });
  return JSON.stringify(request);
};

export const getAddressCount = officerData =>
  officerData ? Object.keys(officerData).filter(v => v.indexOf('DateMove') > -1).length : 0;

const getFieldValue = value => (value === CHOOSE_ADD_YES ? RADIO_VALUE_YES : RADIO_VALUE_NO);

const getDateOfBirth = dob => `${dob.yearField}-${dob.monthField}-${dob.dateField}`;

const isDefaultApplicant = currentOfficerNumber =>
  currentOfficerNumber === '' || currentOfficerNumber === undefined;

const getResidentialStatus = officer => {
  if (
    officer.residentialStatus &&
    officer.residentialStatus.toLowerCase() === RESIDENTIAL_STATUS_OTHER
  ) {
    return officer.residentialStatusOther.toUpperCase();
  }
  return officer.residentialStatus.toUpperCase();
};
const getTitleStatus = officer => {
  if (officer.title && officer.title.toLowerCase() === TITLE_STATUS_OTHER) {
    return officer.titleOther.toUpperCase();
  }
  return officer.title.toUpperCase();
};

export const getAddresses = (officerData, state, currentOfficerNumber) => {
  const address = state.address.toJS().address[currentOfficerNumber];
  const addressPayload = [];
  for (let index = 0; index < getAddressCount(officerData); index++) {
    if (index === 0) {
      if (officerData.ukAddress[SELECT_ADDRESS_FIELD]) {
        addressPayload.push(
          Object.assign(
            {},
            getUkAddressFromPostCode(address.ukAddressSelectedAddress),
            getEffectivePeriod(officerData, index)
          )
        );
      } else if (officerData.ukAddress.local) {
        addressPayload.push(
          Object.assign(
            {},
            getUkAddress(officerData.ukAddress),
            getEffectivePeriod(officerData, index)
          )
        );
      } else {
        addressPayload.push(
          Object.assign(
            {},
            getUkAddressFromPostCode(officerData.ukAddress.givenAddress),
            getEffectivePeriod(officerData, index)
          )
        );
      }
    } else {
      addressPayload.push(getManuallAddresses(officerData, index, address));
    }
  }
  return addressPayload;
};

const getManuallAddresses = (officerData, index, address) => {
  const add = officerData[`${TYPE_UK_ADDRESS}${index}`];
  if (add[CHOOSE_ADD] === CHOOSE_ADD_YES) {
    if (add[SELECT_ADDRESS_FIELD]) {
      return Object.assign(
        getUkAddressFromPostCode(address[`${TYPE_UK_ADDRESS}${index}${SELECTED_ADDRESS}`]),
        getEffectivePeriod(officerData, index)
      );
    } else if (add.local) {
      return Object.assign(getUkAddress(add), getEffectivePeriod(officerData, index));
    }
    return Object.assign(
      {},
      getUkAddressFromPostCode(add.givenAddress),
      getEffectivePeriod(officerData, index)
    );
  } else if (add[CHOOSE_ADD] === CHOOSE_ADD_NO && add.international) {
    return Object.assign(getInternationalAddress(add), getEffectivePeriod(officerData, index));
  }
  return Object.assign(
    {},
    getUkAddressFromPostCode(add.givenAddress),
    getEffectivePeriod(officerData, index)
  );
};

const getEffectivePeriod = (officerData, index) => {
  const dateMovePrefix = index === 0 ? `${TYPE_UK_ADDRESS}` : `${TYPE_UK_ADDRESS}${index}`;
  const dateMove = officerData[`${dateMovePrefix}${DATE_MOVE_SUFFIX}`];
  const dateMoveNextPrefix = `${TYPE_UK_ADDRESS}${index > 1 ? index - 1 : ''}`;
  const dateMoveNext = dateMoveNextPrefix
    ? officerData[`${dateMoveNextPrefix}${DATE_MOVE_SUFFIX}`]
    : null;
  const effectDate = {
    effective_period: {
      from: moment(
        `${dateMove[getFieldName(dateMovePrefix, 'monthField')]}/02/${
          dateMove[getFieldName(dateMovePrefix, 'yearField')]
        }`
      ).format()
    }
  };
  if (index > 0) {
    effectDate.effective_period.to = dateMoveNext
      ? moment(
          `${dateMoveNext[getFieldName(dateMoveNextPrefix, 'monthField')]}/01/${
            dateMoveNext[getFieldName(dateMoveNextPrefix, 'yearField')]
          }`
        ).format()
      : null;
  }
  if (effectDate.effective_period.to === null) {
    delete effectDate.effective_period.to;
  }
  return effectDate;
};

const getFieldName = (prefix, name) => `${prefix}${name}`;

const getBusinessRelationShip = (state, officerData) => {
  const variant = caseSelector.getVariant(state);
  if (variant && variant.toLowerCase() === SOLE_TRADER) {
    return {};
  } else if (variant && variant.toLowerCase() === LIMITED_LIB_PARTNERSHIP) {
    return {
      member: getFieldValue(officerData.companyDirector),
      director: 'N'
    };
  }
  return {
    director: getFieldValue(officerData.companyDirector),
    member: 'N'
  };
};
